import { doc, getDoc } from 'firebase/firestore';
import { db } from '@/config/firebase';
import { blocks } from './blocks';
import { World } from "./world";

/** 
 * Cache TTL in milliseconds (5 minutes)
 * @constant {number}
 */
const CACHE_TTL = 5 * 60 * 1000;

/**
 * Maximum number of chunks to cache in memory
 * @constant {number}
 */
const MAX_MEMORY_CACHE_SIZE = 100;

/**
 * LocalStorage key for persistent cache
 * @constant {string}
 */
const LOCALSTORAGE_KEY = 'chunkModsCache';

/**
 * Queue check interval in milliseconds (1 second)
 * @constant {number}
 */
const QUEUE_CHECK_INTERVAL = 1000;

/**
 * Chunk cache storing modification data and timestamp
 * @typedef {Object} ChunkCache
 * @property {Object.<string, number>} data - coord -> blockId mapping
 * @property {number} timestamp - Last update timestamp
 * @property {number} lastUsed - Last access timestamp for LRU eviction
 */

/** 
 * Map storing cached chunk modifications
 * @type {Map<string, ChunkCache>}
 */
const chunkCache = new Map();

/**
 * Queue for chunks that aren't loaded yet
 * @type {Map<string, {chunk: any, mods: Object}>}
 */
const pendingChunksQueue = new Map();

// Load persistent cache from localStorage on startup
loadPersistentCache();

// Start queue processor
setInterval(processPendingChunksQueue, QUEUE_CHECK_INTERVAL);

/**
 * Processes the pending chunks queue
 */
function processPendingChunksQueue() {
  for (const [chunkKey, entry] of pendingChunksQueue) {
    if (entry.chunk?.loaded) {
      console.log(`Applying pending modifications for chunk ${chunkKey}`);
      applyModifications(entry.world, chunkKey, entry.chunk, entry.mods);
      pendingChunksQueue.delete(chunkKey);
    }
  }
}

/**
 * Loads cache from localStorage
 */
function loadPersistentCache() {
  try {
    const serializedCache = localStorage.getItem(LOCALSTORAGE_KEY);
    if (serializedCache) {
      const parsedCache = JSON.parse(serializedCache);
      for (const [chunkKey, cache] of Object.entries(parsedCache)) {
        chunkCache.set(chunkKey, cache);
      }
      console.log(`Loaded ${Object.keys(parsedCache).length} chunks from persistent cache`);
    }
  } catch (e) {
    console.error('Failed to load cache from localStorage:', e);
  }
}

/**
 * Saves cache to localStorage
 */
function savePersistentCache() {
  try {
    const cacheObj = Object.fromEntries(chunkCache);
    localStorage.setItem(LOCALSTORAGE_KEY, JSON.stringify(cacheObj));
  } catch (e) {
    console.error('Failed to save cache to localStorage:', e);
  }
}

/**
 * Invalidates cache for specific chunk
 * @param {string} chunkKey - Chunk key to invalidate
 */
export function invalidateChunkCache(chunkKey) {
  chunkCache.delete(chunkKey);
  pendingChunksQueue.delete(chunkKey);
  savePersistentCache();
}

/**
 * Invalidates entire cache
 */
export function invalidateAllCache() {
  chunkCache.clear();
  pendingChunksQueue.clear();
  localStorage.removeItem(LOCALSTORAGE_KEY);
}

/**
 * Manages cache size using LRU (Least Recently Used) eviction
 */
function manageCacheSize() {
  if (chunkCache.size > MAX_MEMORY_CACHE_SIZE) {
    const entries = Array.from(chunkCache.entries())
      .sort((a, b) => a[1].lastUsed - b[1].lastUsed);

    const toRemove = Math.floor(entries.length * 0.1);
    for (let i = 0; i < toRemove; i++) {
      chunkCache.delete(entries[i][0]);
    }

    savePersistentCache();
  }
}

/**
 * Applies modifications to a chunk with safety checks
 * @param {World} world - The world instance
 * @param {string} chunkKey - Identifier for the chunk
 * @param {Object} chunk - The chunk object
 * @param {Object.<string, number>} mods - Modifications to apply
 */
function applyModifications(world, chunkKey, chunk, mods) {
  if (!chunk?.loaded) {
    console.log(`Chunk ${chunkKey} is not loaded yet`);
    // Queue the chunk for later processing
    pendingChunksQueue.set(chunkKey, { world, chunk, mods });
    return;
  }

  for (const coord in mods) {
    try {
      const [x, y, z] = coord.split('_').map(c => Math.floor(Number(c)));
      const blockId = mods[coord];

      if (!chunk.inBounds(x, y, z)) {
        console.warn(`Invalid mod coord ${coord} in chunk ${chunkKey}`);
        continue;
      }

      if (blockId === blocks.empty.id) {
        chunk.removeBlock(x, y, z);
      } else {
        chunk.addBlock(x, y, z, blockId);
      }

      world._refreshSurroundings(x, y, z);
      // console.log(`Applied mod ${coord} with blockId ${blockId} to chunk ${chunkKey}`);
    } catch (error) {
      console.error(`Error applying mod ${coord} to chunk ${chunkKey}:`, error);
    }
  }
}

/**
 * Sets up the modification handler for the world
 * @param {World} world - The world instance
 */
export function setupModificationHandler(world) {
  world.on('chunkCreated', async ({ chunkKey, chunk }) => {
    if (!chunkKey || !chunk) {
      console.error('Invalid chunkCreated event');
      return;
    }

    // Prefer world.modCache (populated by startRealtimeModificationSync)
    const realtimeMods = world.modCache?.[chunkKey];
    if (realtimeMods && Object.keys(realtimeMods).length > 0) {
      applyModifications(world, chunkKey, chunk, realtimeMods);
      return;
    }

    const now = Date.now();
    try {
      const cached = chunkCache.get(chunkKey);

      if (cached) {
        cached.lastUsed = now;
        chunkCache.set(chunkKey, cached);

        if (now - cached.timestamp < CACHE_TTL) {
          applyModifications(world, chunkKey, chunk, cached.data);
          return;
        }
      }

      const docRef = doc(db, 'chunkMods', chunkKey);
      const docSnap = await getDoc(docRef);

      if (docSnap.exists()) {
        const mods = docSnap.data().blocks || {};

        chunkCache.set(chunkKey, {
          data: mods,
          timestamp: now,
          lastUsed: now
        });

        manageCacheSize();
        savePersistentCache();

        applyModifications(world, chunkKey, chunk, mods);
      }
    } catch (error) {
      console.error(`Error processing chunk ${chunkKey}:`, error);
      const cached = chunkCache.get(chunkKey);
      if (cached) {
        applyModifications(world, chunkKey, chunk, cached.data);
      }
    }
  });
}